import { useMemo, useEffect } from "react"
import { map, isEmpty } from "lodash"
import { useDispatch } from "react-redux"
import { getTreeLevelData, makeMetricMetadataAndCategories } from "renderers/contributors-table/helpers"
import { sortContributors, createDataGridRows, useNavigationHandlers } from "../helpers"
import { LOADING } from "helpers/data-statuses"
import { createPositionActionFn } from "connected-components/dashboard/helpers/positions"

export const useTableData = (props) => {
  const dispatch = useDispatch()
  const {
    data,
    metrics,
    disableCategories,
    selectedTableLevels,
    setSelectedTableLevels,
    sortDirection,
    status,
  } = props

  const currentLevelData = useMemo(() => getTreeLevelData(data, { path: selectedTableLevels }),
    [data, selectedTableLevels])
  const parentLevelData = useMemo(() => getTreeLevelData(data, { levelsUp: 1, path: selectedTableLevels }),
    [data, selectedTableLevels])

  useEffect(() => {
    // Only validate navigation state when we're not loading
    if (status !== LOADING && data) {
      if (!currentLevelData && !parentLevelData && selectedTableLevels.length > 0) {
        setSelectedTableLevels([])
      } else if (!currentLevelData && parentLevelData) {
        setSelectedTableLevels(selectedTableLevels.slice(0, -1))
      }
    }
  }, [status, data, currentLevelData, parentLevelData, selectedTableLevels, setSelectedTableLevels])

  const { metricMetadata, metricCategories } = useMemo(() =>
    makeMetricMetadataAndCategories(metrics, currentLevelData, disableCategories),
  [metrics, currentLevelData, disableCategories])

  const { currentLevelChildren, isTopLevel, currentLevelLabel } = useMemo(() => ({
    currentLevelChildren: currentLevelData?.children || {},
    currentLevelLabel: currentLevelData?.label,
    isTopLevel: isEmpty(selectedTableLevels),
  }), [currentLevelData, selectedTableLevels])

  const contributorsArray = useMemo(() => {
    return map(currentLevelChildren, (contributor, id) => ({
      ...contributor,
      id,
      action: createPositionActionFn(contributor, dispatch),
    }))
  }, [currentLevelChildren, dispatch])

  // Parse sort direction - handle null case for "no sort" state
  const [sortArrowDirection, sortDirectionPrefix] = sortDirection ? sortDirection.split("_") : [null, null]
  const isAbsoluteSort = !!sortDirectionPrefix

  const { handleSetTableLevel, handleClickContributor } = useNavigationHandlers(props)

  const sortedContributorsArray = useMemo(() => {
    return sortContributors({
      ...props,
      contributorsArray,
      sortArrowDirection,
      isAbsoluteSort,
      metricMetadata,
    })
  }, [props, contributorsArray, sortArrowDirection, isAbsoluteSort, metricMetadata])

  const { rows, pinnedRows } = useMemo(() => {
    return createDataGridRows({
      ...props,
      sortedContributorsArray,
      currentLevelData,
      isTopLevel,
      currentLevelLabel,
    })
  }, [props, sortedContributorsArray, currentLevelData, isTopLevel, currentLevelLabel])

  return {
    currentLevelData,
    parentLevelData,
    metricMetadata,
    metricCategories,
    sortedContributorsArray,
    rows,
    pinnedRows,
    isTopLevel,
    currentLevelLabel,
    contributorsArray,
    sortArrowDirection,
    isAbsoluteSort,
    handleSetTableLevel,
    handleClickContributor,
  }
}
